import clsx from "clsx";
import { useCallback, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import { ContentDivider } from "@atoms/contentDivider";
import { Icon } from "@atoms/icon";
import { Typography } from "@atoms/typography";
import { FormLayout } from "@organisms/formLayout";
import { useAuthStore } from "@templates/authManagement/authStore";
import {
  emailVerificationFormInitialValues,
  emailVerificationFormValidationSchema,
} from "@templates/authManagement/emailVerificationForm/schema";
import { ResendCode } from "@templates/authManagement/resendCode";
import { zodResolver } from "@utils/zod";

import classes from "./styles.module.scss";

import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from "react";

import type { IEmailVerificationFormSchema } from "@templates/authManagement/emailVerificationForm/schema";
import type { IEmailVerificationFormProps } from "@templates/authManagement/emailVerificationForm/types";

export function StudentVerificationForm({
  onSubmit,
  isMutating,
  setIsWrong,
  isWrong,
  isVerificationLoading,
  onResendCode,
}: IEmailVerificationFormProps) {
  const { t } = useTranslation("page");

  const username = useAuthStore(
    useShallow((state) => state.forgetPasswordMeteData.userName),
  );

  const { handleSubmit, setValue, reset } =
    useForm<IEmailVerificationFormSchema>({
      defaultValues: emailVerificationFormInitialValues,
      resolver: zodResolver(emailVerificationFormValidationSchema),
      mode: "onChange",
    });

  const submitFunc = useCallback(
    (values: IEmailVerificationFormSchema) => {
      const { digit1, digit2, digit3, digit4, digit5 } = values;

      onSubmit(digit1 + digit2 + digit3 + digit4 + digit5);
    },
    [onSubmit],
  );

  const [inputs, setInputs] = useState(emailVerificationFormInitialValues);
  const digitRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  const handleBackspace = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Backspace") {
        setIsWrong?.(false);
        const target = event.target as HTMLInputElement;
        const index = (Number.parseInt(target.name.replace("digit", "")) -
          1) as 1 | 2 | 3 | 4;

        // Check if current field is empty and there is a previous field
        if (index > 0 && inputs[target.name as keyof typeof inputs] === "") {
          const prevFieldName = `digit${index}` as const;

          setInputs((prevInputs) => ({ ...prevInputs, [prevFieldName]: "" }));
          setValue(prevFieldName, "");
          digitRefs?.[index - 1]?.current?.focus();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputs, setIsWrong],
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      setIsWrong?.(false);
      const pastedData = event.clipboardData.getData("text").trim();

      if (pastedData.length === 5 && /^\d+$/.test(pastedData)) {
        const digits = [...pastedData];
        let allFilled = true;
        let verificationCode = "";

        for (const [index, digit] of digits.entries()) {
          const fieldName =
            `digit${index + 1}` as keyof IEmailVerificationFormSchema;

          verificationCode += digit;
          setInputs((prevInputs) => ({ ...prevInputs, [fieldName]: digit }));
          setValue(fieldName, digit);

          if (digit.trim() === "") {
            allFilled = false;
          }
        }

        digitRefs?.[4]?.current?.focus();

        if (allFilled) {
          onSubmit(verificationCode);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSubmit, setIsWrong],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      setIsWrong?.(false);
      const { name, value } = event.target;

      // Ensure the value is either empty or a single digit
      if (value === "" || /^\d$/.test(value)) {
        const newInputs: typeof emailVerificationFormInitialValues = {
          ...inputs,
          [name]: value,
        };

        setInputs(newInputs);
        setValue(name as keyof IEmailVerificationFormSchema, value);

        const index = Number.parseInt(name.replace("digit", "")) - 1;

        if (value && index < 4) {
          // Move focus to the next field if the current field is filled
          digitRefs?.[index + 1]?.current?.focus();
        }

        // If this is the last digit and all fields are filled, submit the form
        if (
          index === 4 &&
          Object.values(newInputs).every(
            (digit) => (digit as string)?.trim() !== "",
          )
        ) {
          submitFunc(newInputs);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputs, setIsWrong, submitFunc],
  );

  return (
    <div className={clsx(classes.loginForm)}>
      <div className={clsx(classes.loginForm__head)}>
        <div className={clsx(classes.loginForm__circleOutside)}>
          <div className={clsx(classes.loginForm__circle)}>
            <Icon
              name={"MailCheckFill"}
              fontSize={"fs-32"}
              color={"icon-sub-600"}
            />
          </div>
        </div>
        <Typography fontType={"label-x-large"}>
          {t("auth.emailVerification.title")}
        </Typography>
        <Typography
          fontType={"paragraph-medium"}
          themeColor={"text-sub-600"}
          textWrap
        >
          {t("auth.emailVerification.description", { email: username })}
        </Typography>
      </div>
      <ContentDivider className={"w-100"} />
      <FormLayout
        noCancel
        submitButtonProps={{ type: "submitAndSendCode", className: "w-100" }}
        onSubmit={handleSubmit(submitFunc)}
        isLoading={isMutating}
        noBack
      >
        <div className={classes.codeInputsContainer} dir={"ltr"}>
          {Object.entries(inputs).map(([name], index) => (
            <input
              key={name}
              name={name}
              type={"text"}
              maxLength={1}
              value={inputs[name as keyof typeof inputs]}
              onChange={handleChange}
              onKeyDown={handleBackspace}
              ref={digitRefs[index]}
              onPaste={handlePaste}
              className={clsx(
                classes.codeInput,
                inputs[name as keyof typeof inputs] && classes.activeCodeInput,
                isWrong && classes.errorCodeInput,
              )}
            />
          ))}
        </div>
      </FormLayout>
      <div className={clsx(classes.bottomPart)}>
        <Typography fontType={"paragraph-small"} themeColor={"text-sub-600"}>
          {t("auth.emailVerification.problemInCode")}
        </Typography>
        <ResendCode
          isLoading={isVerificationLoading}
          onResendCode={async () => {
            if (!isMutating) {
              setIsWrong?.(false);
              setInputs(emailVerificationFormInitialValues);
              reset();
              await onResendCode?.();
            }
          }}
          onTimerFinish={() => {
            setInputs(emailVerificationFormInitialValues);
            reset();
          }}
        />
      </div>
    </div>
  );
}
